// Panel Şifreleri (kenar çubuğu sayfası) — temsilcinin Call Agent paneline giriş şifresini sıfırla /
// belirle. Admin VE süpervizör kullanır; süpervizör yalnız KENDİ markalarını görür (brands prop'u +
// sunucu ctx.allowedBrands — asıl kapı sunucuda).
// Şifreler BURADA tutulmaz: her markanın call-agent-proxy'si saklar (scrypt); sunucu tarafı
// (src/api/panel-passwords.js) o proxy'nin /api/admin/* uçlarına aktarır. Liste = proxy'nin
// telefon listesi (giriş yapabilen adlar). Sıfırla → varsayılan <AD>.123 + ilk girişte zorunlu
// değişiklik; Belirle → yöneticinin seçtiği şifre doğrudan geçerli.
// Yönetici ayrıca: "Yeni temsilci" (telefon listesi + temsilci kaydı + panel girişi tek adımda) ve satır
// başına "DARK şifresi" (dahilinin SIP şifresini değiştir) — pages/NewHire.tsx, src/api/new-hire.js.

import { useCallback, useEffect, useState } from 'react';
import { getPanelPasswords, resetPanelPassword, setPanelPassword } from '../api';
import { PANEL_PASSWORD_BRANDS, brandLabel, tzOfBrands } from '../constants';
import { fmtStamp } from '../format';
import { useEscape } from '../hooks';
import { BrandSteps, DarkSecretDialog, NewHireDialog } from './NewHire';
import type { BrandId, PanelPasswordAgent, PanelPasswordList, PanelSyncStep } from '../types';
import { Icon, Modal, Skeleton, cardStyle, ghostBtnStyle, inputStyle, primaryBtnStyle, segStyle } from '../ui';

const GRID = '1fr 120px 150px 90px 92px';
const GRID_ADMIN = '1fr 120px 150px 90px 131px';
const FALLBACK_ERROR = 'İşlem başarısız';

const iconBtn = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  color: 'var(--dim)',
  cursor: 'pointer',
} as const;

const pill = (color: string, bg: string, border: string) =>
  ({
    fontSize: '10.5px',
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    color,
    background: bg,
    border: '1px solid ' + border,
  }) as const;

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--dim)',
  marginBottom: '7px',
  letterSpacing: '.04em',
} as const;

function ErrorLine({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#ef5d5d' }}>
      <Icon name="error" size={15} />
      {text}
    </div>
  );
}

function WarnLine({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: '#e0a23c' }}>
      <Icon name="warning" size={15} />
      {text}
    </div>
  );
}

type Dialog =
  | { kind: 'reset'; agent: PanelPasswordAgent }
  | { kind: 'set'; agent: PanelPasswordAgent }
  | { kind: 'resetDone'; name: string; password: string; persisted: boolean; also: PanelSyncStep[] }
  | { kind: 'setDone'; name: string; persisted: boolean; also: PanelSyncStep[] };

export default function PanelPasswords({ brands, isAdmin = false }: { brands: BrandId[]; isAdmin?: boolean }): JSX.Element {
  const available = PANEL_PASSWORD_BRANDS.filter((b) => brands.includes(b));
  const [brand, setBrand] = useState<BrandId | null>(available[0] || null);
  const [data, setData] = useState<PanelPasswordList | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [newPass, setNewPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [darkFor, setDarkFor] = useState<string | null>(null);
  const grid = isAdmin ? GRID_ADMIN : GRID;

  const load = useCallback(async (b: BrandId) => {
    setLoading(true);
    try {
      setData(await getPanelPasswords(b));
    } catch {
      setData({ ok: false, error: FALLBACK_ERROR });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (brand) void load(brand);
  }, [brand, load]);

  useEscape(!!dialog, () => {
    if (!busy) setDialog(null);
  });

  const open = (d: Dialog) => {
    setDialog(d);
    setNewPass('');
    setShowPass(false);
    setError('');
    setCopied(false);
  };

  const submit = async () => {
    if (!dialog || !brand || busy) return;
    if (dialog.kind !== 'reset' && dialog.kind !== 'set') return;
    const min = data?.minLength || 6;
    if (dialog.kind === 'set' && newPass.length < min) {
      setError('Şifre en az 6 karakter olmalı');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const name = dialog.agent.name;
      const r = dialog.kind === 'reset' ? await resetPanelPassword(brand, name) : await setPanelPassword(brand, name, newPass);
      if (!r.ok) {
        setError(r.error || FALLBACK_ERROR);
        return;
      }
      setNewPass('');
      if (dialog.kind === 'reset') {
        open({ kind: 'resetDone', name: r.name || name, password: r.defaultPassword || '', persisted: r.persisted !== false, also: r.also || [] });
      } else {
        open({ kind: 'setDone', name: r.name || name, persisted: r.persisted !== false, also: r.also || [] });
      }
      void load(brand);
    } catch {
      setError(FALLBACK_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* izin yok — metin zaten ekranda */
    }
  };

  if (!available.length) {
    return (
      <div style={{ ...cardStyle, padding: '18px', fontSize: '13px', color: 'var(--dim)' }}>
        Atanmış markalarınızda panel girişi yok.
      </div>
    );
  }

  const q = search.trim().toLocaleLowerCase('tr');
  const agents = (data?.agents || []).filter((a) => !q || a.name.toLocaleLowerCase('tr').includes(q));
  const tz = tzOfBrands(brand ? [brand] : []);

  return (
    <div style={{ ...cardStyle, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '15px 18px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Panel Şifreleri</div>
          <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>
            Temsilcilerin Call Agent paneline giriş şifreleri
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {available.map((b) => (
            <button key={b} onClick={() => setBrand(b)} style={segStyle(brand === b)}>
              {brandLabel(b)}
            </button>
          ))}
        </div>
        {isAdmin && brand && (
          <button
            onClick={() => setHireOpen(true)}
            style={{ ...primaryBtnStyle, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px' }}
          >
            <Icon name="person_add" size={17} />
            Yeni temsilci
          </button>
        )}
        <button
          onClick={() => brand && void load(brand)}
          title="Yenile"
          disabled={loading}
          style={{ ...iconBtn, width: '36px', height: '36px' }}
        >
          <Icon name="refresh" size={18} />
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 18px',
          borderBottom: '1px solid var(--border)',
          flexWrap: 'wrap',
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Temsilci ara"
          style={{ ...inputStyle, maxWidth: '260px', padding: '8px 11px', fontSize: '12.5px' }}
        />
        <div style={{ fontSize: '11.5px', color: 'var(--faint)', flex: 1, minWidth: '240px', lineHeight: 1.5 }}>
          Sıfırla: şifre varsayılana döner (AD.123), temsilci ilk girişte yeni şifre belirler. Belirle: girdiğiniz
          şifre hemen geçerli olur.
        </div>
      </div>

      {loading && !data && (
        <div style={{ padding: '16px 18px' }}>
          <Skeleton heights={[40, 40, 40]} />
        </div>
      )}

      {data && !data.ok && (
        <div style={{ padding: '16px 18px' }}>
          <ErrorLine text={data.error || FALLBACK_ERROR} />
        </div>
      )}

      {data && data.ok && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: grid,
              gap: '12px',
              padding: '9px 18px',
              borderBottom: '1px solid var(--border)',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '.04em',
              color: 'var(--faint)',
            }}
          >
            <span>TEMSİLCİ</span>
            <span>ŞİFRE</span>
            <span>SON DEĞİŞİKLİK</span>
            <span>DURUM</span>
            <span style={{ textAlign: 'right' }}>İŞLEM</span>
          </div>
          {agents.length === 0 && (
            <div style={{ padding: '16px 18px', fontSize: '12.5px', color: 'var(--dim)' }}>Temsilci bulunamadı</div>
          )}
          {agents.map((a) => (
            <div
              key={a.name}
              style={{
                display: 'grid',
                gridTemplateColumns: grid,
                gap: '12px',
                padding: '11px 18px',
                alignItems: 'center',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', minWidth: 0 }}>{a.name}</span>
              <span style={{ display: 'flex' }}>
                {a.customPassword ? (
                  <span style={pill('#3d8bff', 'rgba(61,139,255,.14)', 'var(--accent-border)')}>Kişisel</span>
                ) : (
                  <span style={pill('var(--dim)', 'var(--surface2)', 'var(--border)')}>Varsayılan</span>
                )}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{a.updatedAt ? fmtStamp(a.updatedAt, tz, true) : '—'}</span>
              <span style={{ display: 'flex' }}>
                {a.locked ? (
                  <span style={pill('#e0a23c', 'rgba(224,162,60,.14)', 'rgba(224,162,60,.3)')}>Kilitli</span>
                ) : (
                  <span style={{ fontSize: '12px', color: 'var(--faint)' }}>—</span>
                )}
              </span>
              <div style={{ display: 'flex', gap: '7px', justifyContent: 'flex-end' }}>
                <button onClick={() => open({ kind: 'reset', agent: a })} title="Varsayılana sıfırla" style={iconBtn}>
                  <Icon name="lock_reset" size={17} />
                </button>
                <button onClick={() => open({ kind: 'set', agent: a })} title="Şifre belirle" style={iconBtn}>
                  <Icon name="key" size={17} />
                </button>
                {isAdmin && (
                  <button onClick={() => setDarkFor(a.name)} title="DARK şifresi" style={iconBtn}>
                    <Icon name="dialpad" size={17} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {hireOpen && brand && (
        <NewHireDialog
          brands={available}
          initialBrand={brand}
          onClose={() => setHireOpen(false)}
          onSaved={(b) => {
            if (b === brand) void load(b);
            else setBrand(b);
          }}
        />
      )}
      {darkFor && brand && <DarkSecretDialog brand={brand} agentName={darkFor} onClose={() => setDarkFor(null)} />}

      {dialog && (
        <Modal onClose={() => !busy && setDialog(null)} maxWidth={420}>
          {dialog.kind === 'reset' && (
            <>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>
                Şifreyi sıfırla
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '18px', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{dialog.agent.name}</span> için panel şifresi
                varsayılana dönecek. Temsilci ilk girişte kendi şifresini belirleyecek.
              </div>
            </>
          )}
          {dialog.kind === 'set' && (
            <>
              <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>Şifre belirle</div>
              <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px' }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{dialog.agent.name}</span> için yeni panel şifresi.
              </div>
              <label style={labelStyle}>YENİ ŞİFRE</label>
              <div style={{ position: 'relative', marginBottom: '18px' }}>
                <input
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void submit();
                  }}
                  type={showPass ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  placeholder="en az 6 karakter"
                  maxLength={data?.maxLength || 128}
                  style={{ ...inputStyle, paddingRight: '42px' }}
                />
                <button
                  onClick={() => setShowPass((v) => !v)}
                  title={showPass ? 'Gizle' : 'Göster'}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--faint)',
                    cursor: 'pointer',
                    display: 'flex',
                    padding: '4px',
                  }}
                >
                  <Icon name={showPass ? 'visibility_off' : 'visibility'} size={18} />
                </button>
              </div>
            </>
          )}
          {(dialog.kind === 'reset' || dialog.kind === 'set') && (
            <>
              {error ? (
                <div style={{ marginBottom: '14px' }}>
                  <ErrorLine text={error} />
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setDialog(null)} disabled={busy} style={{ ...ghostBtnStyle, flex: 1 }}>
                  İptal
                </button>
                <button onClick={() => void submit()} disabled={busy} style={{ ...primaryBtnStyle, flex: 2 }}>
                  {busy ? 'Kaydediliyor…' : dialog.kind === 'reset' ? 'Sıfırla' : 'Kaydet'}
                </button>
              </div>
            </>
          )}
          {dialog.kind === 'resetDone' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
                <Icon name="check_circle" size={22} color="#3ec27a" />
                <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Şifre sıfırlandı</div>
              </div>
              <label style={labelStyle}>GİRİŞ ŞİFRESİ</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <div
                  style={{
                    ...inputStyle,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    userSelect: 'all',
                    flex: 1,
                  }}
                >
                  {dialog.password}
                </div>
                <button onClick={() => void copy(dialog.password)} title="Kopyala" style={{ ...iconBtn, width: '42px', height: 'auto' }}>
                  <Icon name={copied ? 'check' : 'content_copy'} size={18} />
                </button>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--dim)', marginBottom: '14px', lineHeight: 1.5 }}>
                Temsilci bu şifreyle girince kendi şifresini belirlemesi istenir.
              </div>
              {!dialog.persisted && (
                <div style={{ marginBottom: '14px' }}>
                  <WarnLine text="Proxy dosyaya yazamadı — değişiklik proxy yeniden başlayana kadar geçerli" />
                </div>
              )}
              {dialog.also.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>DİĞER MARKALARDA</label>
                  <BrandSteps steps={dialog.also} />
                </div>
              )}
              <button onClick={() => setDialog(null)} style={{ ...primaryBtnStyle, width: '100%' }}>
                Tamam
              </button>
            </>
          )}
          {dialog.kind === 'setDone' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
                <Icon name="check_circle" size={22} color="#3ec27a" />
                <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>Şifre güncellendi</div>
              </div>
              <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{dialog.name}</span> artık yeni şifreyle giriş yapar.
              </div>
              {!dialog.persisted && (
                <div style={{ marginBottom: '14px' }}>
                  <WarnLine text="Proxy dosyaya yazamadı — değişiklik proxy yeniden başlayana kadar geçerli" />
                </div>
              )}
              {dialog.also.length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={labelStyle}>DİĞER MARKALARDA</label>
                  <BrandSteps steps={dialog.also} />
                </div>
              )}
              <button onClick={() => setDialog(null)} style={{ ...primaryBtnStyle, width: '100%' }}>
                Tamam
              </button>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
